'use client'
import type { ChainId } from '@morpho-org/blue-sdk'
import type { Address } from 'viem'

import { isDefined } from '@morpho-org/morpho-ts'
import { CursorPagination } from '@morpho-org/ui-v6/client'
import { TableNoResults } from '@morpho-org/ui-v6/server'
import { useTable } from '@tanstack/react-table'
import { startTransition, useState } from 'react'

import {
  DateRangeFilter,
  dateRangeToTimestamps,
  type DateRangeValue
} from '@/components/common/DateRangeFilter'
import { ErrorDataUnavailable } from '@/components/common/Error'
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table'
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table'
import { TransactionTypeFilter } from '@/components/common/TransactionTypeFilter'
import { VaultTransactionsLayout } from '@/components/VaultPage/components/VaultTransactionsLayout'
import { VAULT_TABLE_10_ROWS_HEIGHT } from '@/components/VaultPage/config'
import { VaultTransactionsPartialNotice } from '@/components/VaultPage/vaultTransactions/components/VaultTransactionsPartialNotice'
import {
  getVaultTransactionsColumns,
  LEGACY_TO_VAULT_TRANSACTION_TYPE,
  PAGE_SIZE,
  TRANSACTION_FILTER_OPTIONS,
  VAULT_TRANSACTION_TYPE_LABELS
} from '@/components/VaultPage/vaultTransactions/config'
import { useVaultTransactionsTableData } from '@/components/VaultPage/vaultTransactions/hooks/useVaultTransactionsTableData'
import { toVaultTransactionsTableRows } from '@/components/VaultPage/vaultTransactions/toVaultTransactionsTableRows'
import { useIsBelowBreakpoint } from '@/hooks/useBreakpoints'
import { useSkeletonRows } from '@/hooks/useSkeletonRows'
import { TransactionType } from '@/types/legacy'

import { vaultTransactionsTableFeatures } from '../../../vaultTransactions/config/VaultTransactions.columns'

export interface VaultTransactionsClientProps {
  address: Address
  chainId: ChainId
}

const columnsDefs = getVaultTransactionsColumns({ userColumn: 'vaultAware' })

const VaultTransactionsClient = ({ address, chainId }: VaultTransactionsClientProps) => {
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string[]>([])
  const [dateRange, setDateRange] = useState<DateRangeValue>()

  const { timestampGte, timestampLte } = dateRangeToTimestamps(dateRange)

  const activeTransactionTypes = transactionTypeFilter
    .map(
      transactionType =>
        // oxlint-disable-next-line typescript/consistent-type-assertions
        TransactionType[transactionType as keyof typeof TransactionType]
    )
    .filter(isDefined)

  const {
    page,
    isLoading,
    isLoadingNextPage,
    error,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useVaultTransactionsTableData({
    address,
    chainId,
    vaultVersion: 'v1',
    limit: PAGE_SIZE,
    filteredTransactionTypes: activeTransactionTypes,
    typeMap: LEGACY_TO_VAULT_TRANSACTION_TYPE,
    scope: { kind: 'activity' },
    timestampGte,
    timestampLte
  })

  const isMediumScreen = useIsBelowBreakpoint('m')

  const formattedTransactions = toVaultTransactionsTableRows({
    page,
    chainId,
    typeLabels: VAULT_TRANSACTION_TYPE_LABELS
  })

  const handleFilterChange = (selectedTransactionTypes: string[]) => {
    startTransition(() => {
      setTransactionTypeFilter(selectedTransactionTypes)
    })
  }

  const transactionsTable = useTable({
    features: vaultTransactionsTableFeatures,
    data: formattedTransactions,
    columns: columnsDefs,
    enableSorting: false
  })

  // First-load + next-page advance — see MarketTransactions.client for rationale.
  const loading = isLoading || isLoadingNextPage
  const hasNoTransactions = !loading && transactionsTable.getRowModel().rows.length === 0
  const partial = page?.partial ?? false
  // A partial page with no rows is unavailable rather than empty.
  const unavailable = error != null || (partial && hasNoTransactions)

  const loadingRows = useSkeletonRows({
    first: PAGE_SIZE,
    totalCount: PAGE_SIZE,
    pagination: { pageIndex: 0, pageSize: PAGE_SIZE },
    columnsDefs
  })

  return (
    <VaultTransactionsLayout
      title="All transactions"
      tableSettingsSlot={
        <div className="gap-sm max-lg:gap-xs flex flex-row flex-wrap items-center">
          <DateRangeFilter
            value={dateRange}
            onChange={setDateRange}
            side="bottom"
            align="end"
            page="vault_activity"
          />
          {isMediumScreen ? (
            <div className="gap-xs flex flex-row items-center">
              <span className="text-3xs text-foreground">Type:</span>
              <TransactionTypeFilter
                selectedTypes={transactionTypeFilter}
                onFilterChange={handleFilterChange}
                options={TRANSACTION_FILTER_OPTIONS}
                side="bottom"
                align="end"
                page="vault_activity"
              />
            </div>
          ) : (
            <TransactionTypeFilter
              selectedTypes={transactionTypeFilter}
              onFilterChange={handleFilterChange}
              options={TRANSACTION_FILTER_OPTIONS}
              side="bottom"
              align="end"
              page="vault_activity"
            />
          )}
        </div>
      }
    >
      <div className="gap-lg flex h-full w-full flex-col">
        <div className="gap-xs flex flex-col">
          {partial && !hasNoTransactions && !loading && <VaultTransactionsPartialNotice />}
          <TableScrollArea className="grid h-full w-full">
            {(!hasNoTransactions || loading) && (
              <Table.Table tableVariant="compact">
                <Table.Thead>
                  {transactionsTable.getHeaderGroups().map(headerGroup => (
                    <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />
                  ))}
                </Table.Thead>
                <Table.Tbody>
                  {!loading &&
                    transactionsTable
                      .getRowModel()
                      .rows.map(row => (
                        <TrBodyRow key={`${row.id}-${row.original.type}`} row={row} />
                      ))}
                  {loading &&
                    loadingRows.map((row, index) => (
                      <TrBodyRow key={`skeleton-${index}`} skeletonRow={row} />
                    ))}
                </Table.Tbody>
              </Table.Table>
            )}
            {hasNoTransactions && !unavailable && !loading && (
              <TableNoResults
                text="No transactions found."
                height={VAULT_TABLE_10_ROWS_HEIGHT}
                style={{ borderRadius: '0' }}
              />
            )}
            {!loading && unavailable && (
              <ErrorDataUnavailable
                minHeight={VAULT_TABLE_10_ROWS_HEIGHT}
                style={{
                  borderRadius: '0'
                }}
              />
            )}
          </TableScrollArea>
          <CursorPagination
            loading={loading}
            error={error}
            pageIndex={pageIndex}
            previousPage={previousPage}
            nextPage={nextPage}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            onPrefetchNext={prefetchNext}
          />
        </div>
      </div>
    </VaultTransactionsLayout>
  )
}

export default VaultTransactionsClient
